/**
 * @file VaccineSchedulingListView.jsx
 * @author Daniel Aguirre
 * @description Vaccine Scheduling View Page
 */

// Dependencies
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';

// Helpers
import {
  formatDate,
  joinArrayObject,
  removeNullValuesFromObject,
  sortStringPlus
} from 'commons/helpers/utils';

import {
  createColumns,
  getSearchApiKeys,
  getSelectFilter,
  getSorterString,
  PAGE_LIMIT
} from '../helper/utils';
import DashboardDetailList from '../dashboardDetailList';

function VaccineSchedulingListView({ dashboardStaffActions, clinicId }) {
  const { t: translate } = useTranslation();
  const [rowData, setRowData] = React.useState([]);

  const { vaccineMonth, dose, totalItems: totalItemsParams } = useParams();
  const [totalItems, setTotalItems] = useState(totalItemsParams);
  const [dataFilter, setDataFilter] = useState(null);
  const [columns, setColumns] = useState([]);
  const [filter, setFilter] = useState(null);
  const [sorter, setSorter] = useState(null);

  const { getVaccineSchedulingListPlus } = dashboardStaffActions;

  const additionalColumns = [
    {
      title: translate('dashboardStaff.serostateClasificationHB'),
      dataIndex: 'clasificacion_seroestado_hepatitis_b',
      filter: {
        listName: 'listaClasificacionSeroestadoHepatitisB',
        searchName: 'filterClasificacionSeroestadoHepatitisB',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('clasificacion_seroestado_hepatitis_b'),
        multiple: 16
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'listaClasificacionSeroestadoHepatitisB',
            searchName: 'filterClasificacionSeroestadoHepatitisB'
          },
          dataIndex: 'clasificacion_seroestado_hepatitis_b'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.vaccinationSchedule'),
      dataIndex: 'esquema_vacunacion',
      filter: {
        listName: 'listaEsquemaVacunacion',
        searchName: 'filterEsquemaVacunacion',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('esquema_vacunacion'),
        multiple: 17
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'listaEsquemaVacunacion',
            searchName: 'filterEsquemaVacunacion'
          },
          dataIndex: 'esquema_vacunacion'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.doseDate1'),
      dataIndex: 'fecha_dosis1',
      filter: {
        listName: 'listaFechaDosis1',
        searchName: 'filterFechaDosis1',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('fecha_dosis1'),
        multiple: 18
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'listaFechaDosis1',
            searchName: 'filterFechaDosis1'
          },
          dataIndex: 'fecha_dosis1'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.doseDate2'),
      dataIndex: 'fecha_dosis2',
      filter: {
        listName: 'listaFechaDosis2',
        searchName: 'filterFechaDosis2',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('fecha_dosis2'),
        multiple: 19
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'listaFechaDosis2',
            searchName: 'filterFechaDosis2'
          },
          dataIndex: 'fecha_dosis2'
        },
        dataFilter
      ),
      ellipsis: true
    }
  ];

  const getetData = (offset, pageLimit) => {
    const pageSize = pageLimit || PAGE_LIMIT;
    const joinFilter = joinArrayObject(filter);
    const joinSorter = joinArrayObject(sorter);
    const data = {
      params: {
        idClinic: clinicId,
        filterDosisSeleccionada: dose,
        filterMesSeleccionado: vaccineMonth,
        limit: pageSize,
        offset,
        ...joinFilter,
        ...joinSorter
      }
    };

    getVaccineSchedulingListPlus(data, (_, resp) => {
      if (resp?.data && resp.data?.totalRegistros != null) {
        const newRowData = resp?.data?.listaDetalles?.map((item) => {
          return {
            ...item,
            fecha_dosis1: item?.fecha_dosis1
              ? formatDate(item.fecha_dosis1)
              : '',
            fecha_dosis2: item?.fecha_dosis2
              ? formatDate(item.fecha_dosis2)
              : ''
          };
        });
        setTotalItems(resp.data?.totalRegistros);
        setRowData(newRowData ?? []);
        setDataFilter(resp.data);
      }
    });
  };

  useEffect(() => {
    if (clinicId !== null) getetData(1);
  }, [clinicId]);

  useEffect(() => {
    const cols = createColumns(translate, dataFilter);
    setColumns(cols);
  }, [rowData, dataFilter]);

  const handleTableChange = (pagination, filters, sorterFilter) => {
    if (filters || sorterFilter) {
      const convertedFilters = getSearchApiKeys(
        [...columns, ...additionalColumns],
        removeNullValuesFromObject(filters)
      );
      const convertedSorts = getSorterString(sorterFilter);
      setFilter(convertedFilters);
      setSorter(convertedSorts);
    }
  };

  return (
    <DashboardDetailList
      columns={columns}
      rowData={rowData}
      totalItems={totalItems}
      onPaginationChange={getetData}
      additionalColumns={additionalColumns}
      onChange={handleTableChange}
      filter={filter}
      sorter={sorter}
    />
  );
}

/**
 * Component definition of proptypes.
 */
VaccineSchedulingListView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getVaccineSchedulingListPlus: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default VaccineSchedulingListView;
